import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Target, Calendar, BarChart3,
  Activity, ArrowUpRight, ArrowDownRight, Eye, History, 
  LineChart, CalendarDays, Briefcase, Building2, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ user, onLogout }) => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentHistory, setAgentHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeHistoryTab, setActiveHistoryTab] = useState('overview');

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const now = new Date();
      let month = now.getMonth() + 1;
      let year = now.getFullYear();
      
      if (selectedMonth === 'previous') {
        if (month === 1) {
          month = 12;
          year = year - 1;
        } else {
          month = month - 1;
        }
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/performance/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month, year },
        timeout: 15000
      });
      setPerformanceData(response.data);
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please refresh.');
      } else {
        toast.error('Failed to fetch performance data');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  const fetchAgentHistory = async (email) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/performance/agent/${email}/history`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { months_back: 12 },
        timeout: 10000
      });
      setAgentHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch agent history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setActiveHistoryTab('overview');
    fetchAgentHistory(agent.email);
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-emerald-600';
    if (growth < 0) return 'text-red-600';
    return 'text-slate-500';
  };

  const getGrowthBg = (growth) => {
    if (growth > 0) return 'bg-emerald-50';
    if (growth < 0) return 'bg-red-50';
    return 'bg-slate-50';
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowUpRight className="w-4 h-4" />;
    if (growth < 0) return <ArrowDownRight className="w-4 h-4" />;
    return null;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-5 h-5 text-emerald-600" />;
    if (trend === 'declining') return <TrendingDown className="w-5 h-5 text-red-600" />;
    return <Activity className="w-5 h-5 text-slate-500" />;
  };

  // Prepare daily sales chart data
  const prepareDailyChartData = () => {
    if (!performanceData?.daily_sales) return [];
    return performanceData.daily_sales.map(item => ({
      date: item.date_short || item.date,
      sales: item.count,
      fullDate: item.date
    }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400">Loading dashboard...</p>
          </div>
        </main>
      </div>
    );
  }

  const data = performanceData;
  const dailyChartData = prepareDailyChartData();

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-6 overflow-auto" data-testid="admin-dashboard">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">Performance Dashboard</h1>
                  <p className="text-slate-400 text-sm">Sales analytics & team performance</p>
                </div>
              </div>
            </div>
            
            {/* Month Selector */}
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-slate-200 rounded-lg h-10 hover:bg-slate-700/50 transition-colors" data-testid="month-selector">
                <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="current">{data?.month_name} {data?.year}</SelectItem>
                <SelectItem value="previous">{data?.prev_month_name}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Total Sales</p>
                    <p className="text-3xl font-bold text-white mt-1">{data?.summary?.total_sales?.toLocaleString()}</p>
                    <div className={`flex items-center gap-1 mt-2 ${getGrowthColor(data?.summary?.growth)}`}>
                      {getGrowthIcon(data?.summary?.growth)}
                      <span className="text-sm font-medium">
                        {data?.summary?.growth > 0 ? '+' : ''}{data?.summary?.growth}% vs last month
                      </span>
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Daily Average</p>
                    <p className="text-3xl font-bold text-white mt-1">{data?.summary?.daily_average}</p>
                    <p className="text-sm text-slate-500 mt-2">{data?.days_for_average || data?.days_counted} days (excl. today)</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Active Agents</p>
                    <p className="text-3xl font-bold text-white mt-1">{data?.summary?.total_agents}</p>
                    <p className="text-sm text-slate-500 mt-2">Team members</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Previous Month</p>
                    <p className="text-3xl font-bold text-white mt-1">{data?.summary?.prev_month_sales?.toLocaleString()}</p>
                    <p className="text-sm text-slate-500 mt-2">{data?.prev_month_name}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <History className="w-5 h-5 text-violet-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Sales Chart */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-500" />
                Daily Sales Trend
                <span className="text-sm font-normal text-slate-400 ml-2">— {data?.month_name} {data?.year}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      dy={10}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)',
                        padding: '8px 12px'
                      }}
                      labelStyle={{ color: '#f8fafc', fontWeight: '600', marginBottom: '4px' }}
                      itemStyle={{ color: '#f59e0b' }}
                      formatter={(value) => [`${value} sales`, 'Sales']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      fill="url(#salesGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Agent Performance Table */}
            <Card className="lg:col-span-2 bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardHeader className="pb-3 border-b border-slate-700/50">
                <CardTitle className="text-base font-semibold text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-amber-500" />
                    Team Performance
                  </span>
                  <Badge variant="outline" className="text-slate-400 border-slate-600 font-normal">
                    {data?.agent_stats?.length} agents
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700/50 bg-slate-800/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Rank</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Agent</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Sales</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Avg/Day</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Growth</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Attendance</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {data?.agent_stats?.map((agent) => (
                        <tr key={agent.email} className="hover:bg-slate-700/30 transition-colors group">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-semibold ${
                              agent.rank === 1 ? 'bg-amber-500/20 text-amber-400' :
                              agent.rank === 2 ? 'bg-slate-600/50 text-slate-300' :
                              agent.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                              'bg-slate-700/50 text-slate-400'
                            }`}>
                              {agent.rank}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-white">{agent.name}</p>
                              <p className="text-xs text-slate-500">{agent.role}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-lg font-semibold text-white">{agent.current_month_sales}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm text-slate-300">{agent.daily_average}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getGrowthBg(agent.growth)} ${getGrowthColor(agent.growth)}`}>
                              {getGrowthIcon(agent.growth)}
                              {agent.growth > 0 ? '+' : ''}{agent.growth}%
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1.5 text-xs">
                              <span className="text-emerald-400 font-medium">{agent.attendance?.present || 0}P</span>
                              <span className="text-slate-600">|</span>
                              <span className="text-red-400 font-medium">{agent.attendance?.absent || 0}A</span>
                              <span className="text-slate-600">|</span>
                              <span className="text-amber-400 font-medium">{agent.attendance?.late || 0}L</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAgentClick(agent)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                              data-testid={`view-agent-${agent.email}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Offer Performance */}
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
              <CardHeader className="pb-3 border-b border-slate-700/50">
                <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-500" />
                  Offer Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {data?.offer_stats?.slice(0, 8).map((offer, idx) => {
                    const maxCount = data?.offer_stats?.[0]?.current_count || 1;
                    const percentage = (offer.current_count / maxCount) * 100;
                    
                    return (
                      <div key={offer.offer}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm text-slate-300 font-medium truncate max-w-[150px]" title={offer.offer}>
                            {offer.offer}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white">{offer.current_count}</span>
                            <span className={`text-xs font-medium ${getGrowthColor(offer.growth)}`}>
                              {offer.growth > 0 ? '+' : ''}{offer.growth}%
                            </span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </main>

      {/* Agent History Modal */}
      <Dialog open={!!selectedAgent} onOpenChange={() => setSelectedAgent(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader className="pb-4 border-b border-slate-700">
            <DialogTitle className="text-lg font-semibold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <span>{selectedAgent?.name}</span>
                <p className="text-sm font-normal text-slate-400">Performance History</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : agentHistory && (
            <div className="space-y-6 pt-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Total Sales (12M)</p>
                  <p className="text-2xl font-bold text-white mt-1">{agentHistory.summary?.total_sales?.toLocaleString()}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Daily Average</p>
                  <p className="text-2xl font-bold text-amber-500 mt-1">{agentHistory.summary?.overall_daily_average}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Best Month</p>
                  <p className="text-xl font-bold text-emerald-400 mt-1">{agentHistory.summary?.best_month?.sales}</p>
                  <p className="text-xs text-slate-500">{agentHistory.summary?.best_month?.month_name}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Trend</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getTrendIcon(agentHistory.summary?.trend)}
                    <span className="text-lg font-semibold text-white capitalize">{agentHistory.summary?.trend}</span>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-slate-700">
                <nav className="flex gap-6">
                  <button
                    onClick={() => setActiveHistoryTab('overview')}
                    className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                      activeHistoryTab === 'overview' 
                        ? 'border-amber-500 text-amber-500' 
                        : 'border-transparent text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4 inline mr-2" />
                    Sales History
                  </button>
                  <button
                    onClick={() => setActiveHistoryTab('attendance')}
                    className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                      activeHistoryTab === 'attendance' 
                        ? 'border-amber-500 text-amber-500' 
                        : 'border-transparent text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <CalendarDays className="w-4 h-4 inline mr-2" />
                    Attendance
                  </button>
                  <button
                    onClick={() => setActiveHistoryTab('offers')}
                    className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                      activeHistoryTab === 'offers' 
                        ? 'border-amber-500 text-amber-500' 
                        : 'border-transparent text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    <Briefcase className="w-4 h-4 inline mr-2" />
                    Top Offers
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {activeHistoryTab === 'overview' && (
                <div className="bg-slate-700/30 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <LineChart className="w-4 h-4 text-amber-500" />
                    Monthly Sales Performance
                  </h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={agentHistory.history?.slice(0, 12).reverse()} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis 
                          dataKey="month_short" 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                        />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1e293b',
                            border: '1px solid #334155',
                            borderRadius: '6px',
                            fontSize: '12px'
                          }}
                          labelStyle={{ color: '#f8fafc' }}
                          itemStyle={{ color: '#f59e0b' }}
                          formatter={(value) => [`${value} sales`, 'Sales']}
                          labelFormatter={(label, payload) => `${payload?.[0]?.payload?.month_name} ${payload?.[0]?.payload?.year}`}
                        />
                        <Bar dataKey="sales" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Monthly Details Table */}
                  <div className="mt-5 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-600">
                          <th className="text-left py-2 text-slate-400 font-medium">Month</th>
                          <th className="text-center py-2 text-slate-400 font-medium">Sales</th>
                          <th className="text-center py-2 text-slate-400 font-medium">Daily Avg</th>
                          <th className="text-center py-2 text-slate-400 font-medium">Working Days</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {agentHistory.history?.slice(0, 6).map((month) => (
                          <tr key={`${month.month}-${month.year}`} className="hover:bg-slate-700/30">
                            <td className="py-2 text-slate-300">{month.month_name} {month.year}</td>
                            <td className="py-2 text-center text-white font-semibold">{month.sales}</td>
                            <td className="py-2 text-center text-slate-300">{month.daily_average}</td>
                            <td className="py-2 text-center text-slate-300">{month.working_days}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeHistoryTab === 'attendance' && (
                <div className="bg-slate-700/30 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-emerald-400" />
                    Monthly Attendance Record
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {agentHistory.history?.slice(0, 6).map((month) => (
                      <div key={`att-${month.month}-${month.year}`} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                        <p className="text-white font-semibold mb-3">{month.month_name} {month.year}</p>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <p className="text-xl font-bold text-emerald-400">{month.attendance?.present || 0}</p>
                            <p className="text-xs text-slate-500">Present</p>
                          </div>
                          <div>
                            <p className="text-xl font-bold text-red-400">{month.attendance?.absent || 0}</p>
                            <p className="text-xs text-slate-500">Absent</p>
                          </div>
                          <div>
                            <p className="text-xl font-bold text-amber-400">{month.attendance?.half_day || 0}</p>
                            <p className="text-xs text-slate-500">Half-day</p>
                          </div>
                          <div>
                            <p className="text-xl font-bold text-violet-400">{month.attendance?.late || 0}</p>
                            <p className="text-xs text-slate-500">Late</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeHistoryTab === 'offers' && (
                <div className="bg-slate-700/30 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-violet-400" />
                    Top Offers by Month
                  </h4>
                  <div className="space-y-3">
                    {agentHistory.history?.slice(0, 4).map((month) => (
                      <div key={`off-${month.month}-${month.year}`} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                        <p className="text-white font-semibold mb-2">{month.month_name} {month.year}</p>
                        <div className="flex flex-wrap gap-2">
                          {month.top_offers?.length > 0 ? month.top_offers.map((offer, idx) => (
                            <Badge key={idx} variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">
                              {offer.offer}: <span className="font-semibold ml-1 text-amber-400">{offer.count}</span>
                            </Badge>
                          )) : (
                            <span className="text-slate-500 text-sm">No data</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
